import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IndianRupee } from "lucide-react";

/**
 * CollectPaymentModal — receptionist records a mid-admission payment
 * (advance / interim collection). Amount accrues to
 * `admission.total_collected` and is auto-netted against the final bill.
 */
export default function CollectPaymentModal({ admission, open, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  if (!admission) return null;

  const submit = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/ipd/admissions/${admission.id}/collect-payment`, {
        amount: n, payment_method: method, reason,
      });
      toast.success(`Payment recorded ₹${n.toLocaleString("en-IN")} · total collected ₹${data.total_collected.toLocaleString("en-IN")}`);
      setAmount(""); setReason(""); setMethod("cash");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="ipd-collect-payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-emerald-600" />
            Collect Payment — {admission.patient_name || "Patient"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Already collected</Label>
            <Input value={`₹${Number(admission.total_collected || 0).toLocaleString("en-IN")}`} disabled />
          </div>
          <div>
            <Label>Amount (₹) *</Label>
            <Input type="number" min="1" step="1" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   placeholder="e.g. 5000" data-testid="ipd-collect-amount" />
          </div>
          <div>
            <Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="ipd-collect-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="check">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} maxLength={250}
                   onChange={(e) => setReason(e.target.value)}
                   placeholder="e.g. Advance for surgery" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !amount}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="ipd-collect-submit">
            {saving ? "Recording…" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
